/* @flow */

import clsx from 'clsx';
import { produce } from 'immer';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, KeyValuePair } from '../../../helpers/jsHelpers/types';
import type { FeedRequestResponseType } from '../../../libs/netgemLibrary/v8/requests/feed';
import type { NETGEM_API_V8_FEED, NETGEM_API_V8_FEED_RAW_ITEM, NetgemApiV8ItemLocation } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_NTG_VIDEO_FEED } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_VIEWINGHISTORY_LOCATION, NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS, VIEWING_HISTORY_TYPE } from '../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { TILE_CONFIG_TYPE } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { SwipeEventData } from '../../swipeable/types';

import { getSectionChannels } from '../../../helpers/channel/helper';
import AccurateTimestamp from '../../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_WEEK, getIso8601DateInSeconds } from '../../../helpers/dateTime/Format';
import { logInfo } from '../../../helpers/debug/debug';
import EpgManager from '../../../helpers/epg/epgManager';
import { type AllSettledPromises, SettledPromiseFulfilled } from '../../../helpers/jsHelpers/promise';
import { LoadableStatus } from '../../../helpers/loadable/loadable';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import PlayingHistoryCache from '../../../helpers/playingHistory/PlayingHistoryCache';
import { EPG, getTileWidthPlusMargin } from '../../../helpers/ui/constants';
import { areFavoriteListsDifferent } from '../../../helpers/ui/section/comparisons';
import { createSectionPagination } from '../../../helpers/ui/section/Pagination';
import { getTileConfig, getTileTypeClass } from '../../../helpers/ui/section/tile';
import { SectionDisplayType } from '../../../helpers/ui/section/types';
import { getMostRecentlyPlayedEpisode } from '../../../helpers/viewingHistory/ViewingHistory';
import ViewingHistoryCache from '../../../helpers/viewingHistory/ViewingHistoryCache';
import WishlistCache from '../../../helpers/wishlist/WishlistCache';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { filterFeed, filterViewingHistoryFeed, mergeFeeds, setFeedScore } from '../../../libs/netgemLibrary/v8/helpers/Feed';
import { getLocationType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { FeedProviderKind } from '../../../libs/netgemLibrary/v8/types/Feed';
import { LocalFeed, type NETGEM_API_V8_SECTION } from '../../../libs/netgemLibrary/v8/types/Section';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { cleanUpViewingHistory } from '../../../redux/netgemApi/actions/personalData/viewingHistory';
import sendV8LocationCatchupForAssetRequest from '../../../redux/netgemApi/actions/v8/catchupForAsset';
import sendV8LocationEpgForAssetRequest from '../../../redux/netgemApi/actions/v8/epgForAsset';
import sendV8SectionFeedRequest from '../../../redux/netgemApi/actions/v8/feed';
import sendV8LocationGameForAssetRequest from '../../../redux/netgemApi/actions/v8/gameForAsset';
import sendV8LocationVodForAssetRequest from '../../../redux/netgemApi/actions/v8/vodForAsset';
import { resetGridSectionId, updateGridSectionId, updateSectionPageIndex } from '../../../redux/ui/actions';
import ButtonBack from '../../buttons/ButtonBack';
import { PictoArrowLeft, PictoArrowRight } from '../../pictos/Element';
import Swipeable from '../../swipeable/swipeable';
import VisibilityWaypoint from '../../waypoint/VisibilityWaypoint';
import Item from '../item/Item';
import PlaceholderItem from '../item/PlaceholderItem';
import SortAndFilter from '../sortAndFilter/SortAndFilter';
import { type SortAndFilterType, SortDirection, SortKind } from '../sortAndFilter/type';
import './Section.css';
import {
  checkSources,
  compareDate,
  createGameFeedItem,
  disableHotKeys,
  enableHotKeys,
  getLocalPath,
  getPageStepFromKeyboardModifiers,
  getPlaceholderItemCount,
  getTimestampFromRange,
  getValuesFromQueryString,
  getViewingHistoryPromiseGenerator,
  getWishlistPromiseGenerator,
  isItemTypeMatching,
  locationTypeToQueryType,
  queryTypeToLocationType,
  showDebugInfo,
} from './helper';
import {
  type CompleteSectionPropType,
  type DefaultProps,
  type LocalPlayedItemType,
  MAX_Z_INDEX,
  PAGE_DISPLAY_COUNT,
  PagePosition,
  type ReduxSectionDispatchToPropsType,
  type ReduxSectionReducerStateType,
  SWIPE,
  type SectionPropType,
  type SectionStateType,
  StandardSectionType,
} from './SectionConstsAndTypes';

const InitialState = Object.freeze({
  displayType: SectionDisplayType.Regular,
  feed: null,
  focusedItemIndex: 0,
  isCollapsed: false,
  isDebugModePlus: false,
  isLive: false,
  isLoading: true,
  isSwiping: false,
  itemCountPerPage: -1,
  maxPageIndex: -1,
  pageIndex: 0,
  sortAndFilter: null,
  updateDimensionsNeeded: true,
});

class SectionView extends React.PureComponent<CompleteSectionPropType, SectionStateType> {
  abortController: AbortController;
  isVisible: boolean;
  liveFeedTimer: TimeoutID | null;
  sectionChannels: Set<string> | null;
  sectionName: string;
  sectionItemWidth: number;
  sectionType: StandardSectionType | null;
  slider: HTMLElement | null;
  sliderPositions: Array<number>;
  swipeTimeout: TimeoutID | null;
  tileConfig: TILE_CONFIG_TYPE;

  static defaultProps: DefaultProps = {
    cardData: null,
    hubItem: null,
    innerRef: null,
    isGridAvenue: false,
    isInExploreModal: false,
    onItemClick: undefined,
    searchString: null,
  };

  constructor(props: CompleteSectionPropType) {
    super(props);

    const {
      cardData,
      gridSectionId,
      section,
      section: { id },
    } = props;
    const tileConfig = getTileConfig(section);
    const { type } = tileConfig;

    this.abortController = new AbortController();
    this.isVisible = false;
    this.liveFeedTimer = null;
    this.sectionChannels = null;
    this.sectionName = id;
    this.sectionItemWidth = getTileWidthPlusMargin(type);
    this.sectionType = null;
    this.slider = null;
    this.sliderPositions = [];
    this.swipeTimeout = null;
    this.tileConfig = tileConfig;

    const isCollapsed = !cardData && gridSectionId !== null && id !== gridSectionId;

    let displayType = SectionDisplayType.Regular;
    if (isCollapsed) {
      displayType = SectionDisplayType.Collapsed;
    } else if (id === gridSectionId) {
      displayType = SectionDisplayType.Grid;
    }

    this.state = {
      ...InitialState,
      displayType,
      isCollapsed,
    };
  }

  componentDidMount() {
    const { innerRef } = this.props;
    const { displayType } = this.state;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }

    Messenger.on(MessengerEvents.AVENUE_RESET, this.reset);

    window.addEventListener('resize', this.updateDimensions, { passive: true });

    if (displayType === SectionDisplayType.Grid) {
      enableHotKeys(this.handleExitGridViewHotKey);
    }

    this.loadData();
  }

  componentDidUpdate(prevProps: CompleteSectionPropType, prevState: SectionStateType) {
    const {
      favoriteList,
      gridSectionId,
      playingHistory,
      playingHistoryStatus,
      searchString,
      section: { id: sectionId },
      viewingHistory,
      viewingHistoryStatus,
      wishlist,
      wishlistStatus,
    } = this.props;
    const { displayType, isCollapsed, itemCountPerPage } = this.state;
    const {
      favoriteList: prevFavoriteList,
      gridSectionId: prevGridSectionId,
      playingHistory: prevPlayingHistory,
      searchString: prevSearchString,
      viewingHistory: prevViewingHistory,
      wishlist: prevWishlist,
    } = prevProps;
    const { displayType: prevDisplayType, isCollapsed: prevIsCollapsed, itemCountPerPage: prevItemCountPerPage } = prevState;
    const { sectionType } = this;

    if (gridSectionId !== prevGridSectionId) {
      this.updateIsCollapsed();
    }

    if (sectionId === 'wishList_vod' && areFavoriteListsDifferent(prevFavoriteList, favoriteList)) {
      this.buildRegularSection();
    }

    if (displayType !== prevDisplayType) {
      if (displayType === SectionDisplayType.Grid) {
        enableHotKeys(this.handleExitGridViewHotKey);
      } else {
        disableHotKeys(this.handleExitGridViewHotKey);
      }
    }

    if (isCollapsed !== prevIsCollapsed) {
      if (isCollapsed) {
        this.collapse();
      } else {
        this.show();
      }
    }

    if (itemCountPerPage !== prevItemCountPerPage) {
      this.createPageList(prevItemCountPerPage);
    }

    if (sectionType === StandardSectionType.Wishlist && wishlistStatus !== LoadableStatus.NotInitialized && wishlist !== prevWishlist) {
      this.buildWishlistSection();
      return;
    }

    if (sectionType === StandardSectionType.ViewingHistory && viewingHistoryStatus !== LoadableStatus.NotInitialized && viewingHistory !== prevViewingHistory) {
      this.buildViewingHistorySection();
      return;
    }

    if (sectionType === StandardSectionType.PlayingHistory && playingHistoryStatus !== LoadableStatus.NotInitialized && playingHistory !== prevPlayingHistory) {
      this.buildPlayingHistorySection();
      return;
    }

    if (searchString !== prevSearchString) {
      this.loadData();
      return;
    }

    this.componentDidUpdateFeedUpdate(prevProps, prevState);
  }

  componentWillUnmount() {
    const { abortController, liveFeedTimer } = this;
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(null);
    }

    abortController.abort('Component Section will unmount');

    Messenger.off(MessengerEvents.AVENUE_RESET, this.reset);
    Messenger.off(MessengerEvents.REFRESH_RECORDINGS_SECTION, this.forceRefresh);
    window.removeEventListener('resize', this.updateDimensions, { passive: true });
    disableHotKeys(this.handleExitGridViewHotKey);

    this.resetSwipeTimeout();

    // Clear timer
    if (liveFeedTimer) {
      clearTimeout(liveFeedTimer);
    }
  }

  componentDidUpdateFeedUpdate = (prevProps: CompleteSectionPropType, prevState: SectionStateType) => {
    const {
      avenueIndex,
      dataLoadedCallback,
      hubItem,
      section: { id },
      sectionIndex,
    } = this.props;
    const { displayType, feed, isCollapsed } = this.state;
    const { avenueIndex: prevAvenueIndex, hubItem: prevHubItem } = prevProps;
    const { displayType: prevDisplayType, feed: prevFeed } = prevState;

    if (avenueIndex !== prevAvenueIndex || (hubItem !== prevHubItem && !hubItem) || (!prevFeed && feed) || (feed && feed.length !== prevFeed?.length)) {
      // Section has already been built in previous avenue or sub-avenue
      dataLoadedCallback(id, sectionIndex, avenueIndex, feed?.length ?? 0);
    }

    if (feed?.length === 0 && displayType === SectionDisplayType.Regular && prevDisplayType === SectionDisplayType.Grid) {
      // Section has been emptied while in grid view, it must be collapsed now it changed back to regular view
      this.collapse();
    }

    if (feed && prevFeed !== feed) {
      if (feed.length === 0) {
        // Feed changed from non-empty to empty
        if (displayType === SectionDisplayType.Grid) {
          // First exit grid view, then collapse section
          this.exitGridView();
        } else {
          // Collapse empty section
          this.collapse();
        }
        return;
      }

      // Feed is not empty

      if (!isCollapsed) {
        this.show();
      }

      if (!prevFeed || prevFeed.length !== feed.length) {
        // Feed has changed: re-render everything
        this.createPageList();
      }
    }
  };

  updateIsCollapsed: () => void = () => {
    const {
      gridSectionId,
      section: { id },
    } = this.props;

    this.setState({ isCollapsed: gridSectionId !== null && id !== gridSectionId });
  };

  resetSwipeTimeout: () => void = () => {
    if (this.swipeTimeout) {
      clearTimeout(this.swipeTimeout);
      this.swipeTimeout = null;
    }
  };

  // Exit grid view
  handleExitGridViewHotKey: (event: SyntheticKeyboardEvent<HTMLElement>) => void = (event) => {
    const { displayType } = this.state;

    if (displayType === SectionDisplayType.Grid) {
      event.preventDefault();
      event.stopPropagation();
      this.exitGridView();
    }
  };

  setEmptySectionLoaded: () => void = () => {
    this.setSectionLoaded([]);
  };

  setSectionLoaded: (feed: NETGEM_API_V8_FEED) => void = (feed) => {
    const {
      avenueIndex,
      dataLoadedCallback,
      section: { id },
      sectionIndex,
    } = this.props;

    this.setState(
      produce((draft) => {
        draft.feed = feed;
        draft.isLoading = false;
      }),
    );

    // Notify avenue and tell it how many items were loaded
    dataLoadedCallback(id, sectionIndex, avenueIndex, feed.length ?? 0);
  };

  updateLiveSection: () => void = () => {
    const { hubItem, section } = this.props;
    const { sectionChannels } = this;

    if (sectionChannels !== null && !EpgManager.isRefreshing()) {
      const newLiveFeed = EpgManager.getLiveFeed(section, sectionChannels, hubItem);

      this.setState(
        produce((draft) => {
          draft.feed = newLiveFeed;
          draft.isLoading = false;
        }),
      );
    }

    this.liveFeedTimer = setTimeout(this.updateLiveSection, EpgManager.isRefreshing() ? EPG.IntervalLiveFeedEpgRefreshing : EPG.IntervalLiveFeedEpgReady);
  };

  buildWishlistSection = (): void => {
    const {
      channels,
      localSendV8LocationCatchupForAssetRequest,
      localSendV8LocationEpgForAssetRequest,
      localSendV8LocationGameForAssetRequest,
      localSendV8LocationVodForAssetRequest,
      recordingsListFeed,
      section: {
        model,
        model: { scoring: globalScoring, slice: globalSlice },
      },
      sessionId,
      wishlist,
      wishlistStatus,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (wishlistStatus === LoadableStatus.NotInitialized) {
      // Not yet retrieved from server
      return;
    }

    if (wishlistStatus === LoadableStatus.Error || wishlist.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    // Build promises for every source
    const promises: Array<Promise<FeedRequestResponseType>> = [];
    let { sources = [] } = model;
    const now = AccurateTimestamp.now();

    // Check if feed model is not an aggregation of sources
    sources = checkSources(sources, model);

    if (sources.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    for (let i = 0; i < sources.length; i++) {
      const {
        [i]: { uri },
      } = sources;
      const promiseGenerator = getWishlistPromiseGenerator(
        localSendV8LocationGameForAssetRequest,
        localSendV8LocationCatchupForAssetRequest,
        localSendV8LocationEpgForAssetRequest,
        localSendV8LocationVodForAssetRequest,
        recordingsListFeed,
        uri,
        now,
        signal,
      );
      wishlist.forEach((item) => promises.push(promiseGenerator(item)));
    }

    // Wait for all promises to resolve
    Promise.allSettled(promises).then((results: AllSettledPromises<FeedRequestResponseType>) => {
      if (signal.aborted || sources === undefined) {
        return;
      }

      const { length: itemCount } = wishlist;

      const feedArray = [];
      for (let i = 0; i < sources.length; i++) {
        const {
          [i]: { filter, scoring, slice, uri },
        } = sources;
        const startIndex = i * itemCount;
        const endIndex = startIndex + itemCount;
        const { q: expectedType } = getValuesFromQueryString(uri, ['q']);

        const sourceFeed = [];
        const addToSourceFeed = (item: NETGEM_API_V8_FEED_RAW_ITEM) => {
          if (isItemTypeMatching(item, expectedType)) {
            sourceFeed.push(item);
          }
        };

        for (let j = startIndex; j < endIndex; ++j) {
          const result = results[j];

          if (result && result.status === SettledPromiseFulfilled) {
            result.value?.result?.feed?.forEach(addToSourceFeed);
          }
        }

        if (sourceFeed.length > 0) {
          let filteredFeed: NETGEM_API_V8_FEED = filterFeed(sourceFeed, channels, filter, slice);
          if (scoring) {
            filteredFeed = setFeedScore(filteredFeed, scoring, { sessionId });
          }
          feedArray.push(filteredFeed);
        }
      }

      this.setSectionLoaded(mergeFeeds(feedArray, globalScoring, { sessionId }, globalSlice));
    });
  };

  parseViewingHistory: (viewingHistory: VIEWING_HISTORY_TYPE) => { playedItemIds: KeyValuePair<Set<string>>, playedItemsMap: KeyValuePair<LocalPlayedItemType>, playedSeries: Set<string> } = (
    viewingHistory,
  ) => {
    const playedItemIds: KeyValuePair<Set<string>> = {};
    const playedItemsMap: KeyValuePair<LocalPlayedItemType> = {};
    const playedSeries: Set<string> = new Set();

    viewingHistory.forEach((item) => {
      const { episodes, id, playeditems } = item;
      let itemsToAdd: NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS | null = null;

      if (episodes && episodes.length > 0) {
        // Series
        playedSeries.add(id);

        const { playeditems: items } = getMostRecentlyPlayedEpisode(episodes);
        if (items) {
          itemsToAdd = items;
        }
      } else if (playeditems) {
        // Program
        itemsToAdd = playeditems;
      }

      if (itemsToAdd !== null) {
        // To prevent Flow from complaining when using locationId key
        const safeItemsToAdd = itemsToAdd;

        Object.keys(safeItemsToAdd).forEach((locationId) => {
          const playedItem: ?NETGEM_API_VIEWINGHISTORY_LOCATION = safeItemsToAdd[locationId];
          const locationType = getLocationType(locationId);

          if (playedItem && locationType) {
            const queryType = locationTypeToQueryType(locationType);
            if (queryType) {
              if (!playedItemIds[queryType]) {
                playedItemIds[queryType] = new Set<string>();
              }
              playedItemIds[queryType].add(locationId);

              playedItemsMap[locationId] = {
                ...playedItem,
                id,
              };
            }
          }
        });
      }
    });

    return {
      playedItemIds,
      playedItemsMap,
      playedSeries,
    };
  };

  buildViewingHistoryFeedFromPromises: (
    sources: Array<NETGEM_API_V8_NTG_VIDEO_FEED>,
    promiseCountByType: KeyValuePair<number>,
    playedItemsMap: KeyValuePair<LocalPlayedItemType>,
    playedSeries: Set<string>,
    results: AllSettledPromises<FeedRequestResponseType>,
  ) => void = (sources, promiseCountByType, playedItemsMap, playedSeries, results) => {
    const {
      channels,
      localCleanUpViewingHistory,
      section: {
        model: { scoring: globalScoring, slice: globalSlice },
      },
      sessionId,
      viewingHistory,
      viewingHistoryStatus,
    } = this.props;

    const searchedLocationTypes = new Set<NetgemApiV8ItemLocation>();
    const feedArray = [];

    const noIdPlayedItemsMap: NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS = {};
    Object.keys(playedItemsMap).forEach((key) => {
      // Get all properties except id
      const { channelId, date = '', percent = 0, position = 0 } = playedItemsMap[key] || {};

      noIdPlayedItemsMap[key] = {
        channelId,
        date,
        percent,
        position,
      };
    });

    let startIndex = 0;
    for (let i = 0; i < sources.length; i++) {
      const {
        [i]: { filter, scoring, slice, uri },
      } = sources;
      const { q: queryType } = getValuesFromQueryString(uri, ['q']);

      const locationType = queryTypeToLocationType(queryType);
      if (locationType) {
        searchedLocationTypes.add(locationType);
      }

      if (queryType && promiseCountByType[queryType]) {
        const endIndex = startIndex + promiseCountByType[queryType];
        const sourceFeed = [];
        const addToSourceFeed = (item: NETGEM_API_V8_FEED_RAW_ITEM) => sourceFeed.push(item);

        for (let j = startIndex; j < endIndex; ++j) {
          const { [j]: result } = results;
          const { status, value } = result;

          if (status === SettledPromiseFulfilled) {
            value?.result?.feed?.forEach(addToSourceFeed);
          }
        }

        startIndex = endIndex;

        if (sourceFeed.length > 0) {
          let filteredFeed: NETGEM_API_V8_FEED = filterFeed(sourceFeed, channels, filter, slice);
          if (scoring) {
            filteredFeed = setFeedScore(filteredFeed, scoring, {
              playedItemsMap: noIdPlayedItemsMap,
              sessionId,
            });
          }
          const validFeed = filterViewingHistoryFeed(filteredFeed, noIdPlayedItemsMap, playedSeries);
          feedArray.push(validFeed);
        }
      }
    }

    const feed = mergeFeeds(feedArray, globalScoring, { sessionId }, globalSlice);

    this.setSectionLoaded(feed);

    if (viewingHistoryStatus === LoadableStatus.Loaded) {
      localCleanUpViewingHistory(viewingHistory, feed, searchedLocationTypes);
    }
  };

  buildViewingHistorySection: () => void = () => {
    const {
      localSendV8LocationCatchupForAssetRequest,
      localSendV8LocationVodForAssetRequest,
      recordingsListFeed,
      section: { model },
      viewingHistory,
      viewingHistoryStatus,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (viewingHistoryStatus === LoadableStatus.NotInitialized) {
      // Not yet retrieved from server
      return;
    }

    if (viewingHistoryStatus === LoadableStatus.Error || viewingHistory.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    /*
     * Parse viewing history to retrieve items Ids
     * playedItemIds:  map of sets of location Ids grouped by type (catchup, recording, etc.)
     * playedItemsMap: map from location Id (catchup, recording, etc.) to viewing history item Id (i.e. series or program Id)
     * playedSeries:   set of series Id
     */
    const { playedItemIds, playedItemsMap, playedSeries } = this.parseViewingHistory(viewingHistory);

    // Build promises for every source
    const promises: Array<Promise<FeedRequestResponseType>> = [];
    let { sources = [] } = model;
    const now = AccurateTimestamp.now();

    // Check if feed model is not an aggregation of sources
    sources = checkSources(sources, model);

    if (sources.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    const promiseCountByType: KeyValuePair<number> = {};

    for (let i = 0; i < sources.length; i++) {
      const {
        [i]: { uri },
      } = sources;
      const { allChannels, q: queryType, range } = getValuesFromQueryString(uri, ['allChannels', 'q', 'range']);
      const thresholdTime = getTimestampFromRange(range);

      if (queryType && playedItemIds[queryType]) {
        promiseCountByType[queryType] = 0;
        const promiseGenerator = getViewingHistoryPromiseGenerator(
          localSendV8LocationCatchupForAssetRequest,
          localSendV8LocationVodForAssetRequest,
          recordingsListFeed,
          uri,
          now,
          allChannels !== '0',
          queryType,
          signal,
        );

        if (promiseGenerator !== null) {
          playedItemIds[queryType].forEach((locationId) => {
            const item = playedItemsMap[locationId];
            const { date } = item;

            if (item && getIso8601DateInSeconds(date) >= thresholdTime) {
              promiseCountByType[queryType] += 1;
              promises.push(promiseGenerator(item));
            }
          });
        }
      }
    }

    if (promises.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    // Wait for all promises to resolve/reject
    Promise.allSettled(promises).then((results: AllSettledPromises<FeedRequestResponseType>) => {
      if (signal.aborted || sources === undefined) {
        return;
      }

      this.buildViewingHistoryFeedFromPromises(sources, promiseCountByType, playedItemsMap, playedSeries, results);
    });
  };

  buildPlayingHistorySection: () => void = () => {
    const { localSendV8LocationGameForAssetRequest, playingHistory, playingHistoryStatus } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (playingHistoryStatus === LoadableStatus.NotInitialized) {
      // Not yet retrieved from server
      return;
    }

    if (playingHistoryStatus === LoadableStatus.Error || playingHistory.length === 0) {
      this.setEmptySectionLoaded();
      return;
    }

    const promises: Array<Promise<FeedRequestResponseType>> = [];
    const now = AccurateTimestamp.now();

    playingHistory.forEach(({ channelId, programId }) => {
      promises.push(localSendV8LocationGameForAssetRequest(programId, now, MILLISECONDS_PER_WEEK, [channelId], signal));
    });

    // Wait for all promises to resolve/reject
    Promise.allSettled(promises).then((results: AllSettledPromises<FeedRequestResponseType>) => {
      if (signal.aborted) {
        return;
      }

      const feed: NETGEM_API_V8_FEED = [];

      results.forEach((result) => {
        if (result.status === SettledPromiseFulfilled) {
          result.value.result?.feed?.forEach((item) => {
            const feedItem = createGameFeedItem(item);
            if (feedItem !== null) {
              feed.push(feedItem);
            }
          });
        }
      });

      this.setSectionLoaded(feed);
    });
  };

  buildRegularSection: () => void = () => {
    const { localSendV8SectionFeedRequest, searchString, section } = this.props;
    const {
      abortController: { signal },
    } = this;

    // Unsubscribe from event in case the section has already been built
    Messenger.off(MessengerEvents.REFRESH_RECORDINGS_SECTION, this.forceRefresh);

    localSendV8SectionFeedRequest(section, searchString, signal)
      .then((feed: NETGEM_API_V8_FEED) => {
        signal.throwIfAborted();

        this.setSectionLoaded(feed);

        // Refresh section on every NPVR event (start/stop record)
        Messenger.on(MessengerEvents.REFRESH_RECORDINGS_SECTION, this.forceRefresh);
      })
      .catch((error) => ignoreIfAborted(signal, error, this.setEmptySectionLoaded));
  };

  // Only used for recordings and scheduled recordings sections
  forceRefresh: () => void = () => {
    this.buildRegularSection();
  };

  loadData: () => void = () => {
    const {
      channels,
      isRegisteredAsGuest,
      section,
      section: { model },
      state,
    } = this.props;

    // Reset section display: go back to first page
    this.reset(true);

    let { provider } = model;
    let uri: string = '';
    let path: ?string = null;

    if (provider === FeedProviderKind.Aggregation) {
      const { sources = [] } = model;

      if (!sources || sources.length === 0) {
        logInfo(`Missing sources for section ${section.id}`);
        this.setEmptySectionLoaded();
        return;
      }

      // Pick first model
      [{ provider, uri }] = sources;
    } else {
      ({ uri = '' } = model);
    }

    if (provider === FeedProviderKind.Local) {
      /*
       * Examples:
       *  local://channels/list --> same as ?event=live
       *  local://channels/list?event=live&channels={channelList}
       *  local://channels/list?event=next&channels={channelList}
       *  local://wishlist/?q=scheduledevent
       *  local://wishlist/?q=(catchup|recording|svod|tvod|est)
       *  local://viewinghistory/?q=(catchup|recording)&range=P15D
       */
      path = getLocalPath(uri);
    }

    switch (provider) {
      case FeedProviderKind.Local:
        // Local provider

        switch (path) {
          case LocalFeed.Channels: {
            // Feed of local channels
            const { channels: channelsParam, event } = getValuesFromQueryString(uri, ['channels', 'event']);
            this.sectionChannels = getSectionChannels(section, channelsParam, channels, state);

            if (event !== null) {
              // Live feed
              this.sectionType = StandardSectionType.Live;
              this.setState({ isLive: true });
              this.updateLiveSection();
            } else {
              // Channels feed (e.g. FAST channels)
              this.sectionType = StandardSectionType.Regular;
              const feed = EpgManager.buildFakeChannelsSection(section, this.sectionChannels);
              if (feed !== null) {
                if (feed.length === 0) {
                  this.setEmptySectionLoaded();
                } else {
                  this.setSectionLoaded(feed);
                }
              }
            }
            break;
          }

          case LocalFeed.ViewingHistory:
            // Viewing history (TV)
            if (isRegisteredAsGuest) {
              this.collapse();
              this.setEmptySectionLoaded();
            } else {
              this.sectionType = StandardSectionType.ViewingHistory;
              this.buildViewingHistorySection();
            }
            break;

          case LocalFeed.Wishlist:
            // TV programs flagged with the gem icon
            if (isRegisteredAsGuest) {
              this.collapse();
              this.setEmptySectionLoaded();
            } else {
              this.sectionType = StandardSectionType.Wishlist;
              this.buildWishlistSection();
            }
            break;

          case LocalFeed.PlayingHistory:
            // Playing history (gaming)
            if (isRegisteredAsGuest) {
              this.collapse();
              this.setEmptySectionLoaded();
            } else {
              this.sectionType = StandardSectionType.PlayingHistory;
              this.buildPlayingHistorySection();
            }
            break;

          default:
            Messenger.emit(
              MessengerEvents.NOTIFY_ERROR,
              <div className='toastContent'>
                <div>Type de feed inconnu&nbsp;:</div>
                <div>
                  <b>{path}</b>
                </div>
              </div>,
              undefined,
              `Type de feed inconnu&nbsp;: ${path ?? ''}`,
            );
            break;
        }
        break;

      case FeedProviderKind.NtgVideo:
      default:
        // Regular sections (replay, future, live)
        this.sectionType = StandardSectionType.Regular;
        this.buildRegularSection();
    }
  };

  reset: (skipGridViewExit?: boolean) => void = (skipGridViewExit) => {
    const { isGridAvenue } = this.props;
    const { displayType } = this.state;

    if (displayType === SectionDisplayType.Regular) {
      this.goToPage(0);
    } else if (!isGridAvenue && skipGridViewExit !== true) {
      this.exitGridView();
    }
  };

  calculateItemCountPerPage: () => number = () => {
    const { sectionItemWidth, slider } = this;

    if (!slider || !(slider.parentElement instanceof HTMLElement) || sectionItemWidth <= 0) {
      return -1;
    }

    return Math.max(1, Math.floor(slider.parentElement.offsetWidth / sectionItemWidth));
  };

  updateDimensions: () => void = () => {
    const { isVisible } = this;

    if (!isVisible) {
      this.setState({ updateDimensionsNeeded: true });
      return;
    }

    const itemCountPerPage = this.calculateItemCountPerPage();

    this.setState({
      itemCountPerPage,
      updateDimensionsNeeded: itemCountPerPage === -1,
    });
  };

  createPageList: (previousItemCountPerPage?: number) => void = (previousItemCountPerPage) => {
    const { feed, itemCountPerPage, pageIndex: previousPageIndex } = this.state;
    const { sectionItemWidth } = this;

    if (!feed) {
      return;
    }

    if (itemCountPerPage === -1) {
      this.setState({ updateDimensionsNeeded: true });
      return;
    }

    const { maxPageIndex, pageIndex, sliderPositions } = createSectionPagination(feed, sectionItemWidth, itemCountPerPage, previousPageIndex, previousItemCountPerPage);

    this.sliderPositions = sliderPositions;
    this.setState(
      {
        maxPageIndex,
        pageIndex,
      },
      () => this.goToPage(pageIndex, true, this.checkSavedPageIndex),
    );
  };

  handleEnterGridViewButtonOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.enterGridView();
  };

  handleExitGridViewButtonOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.exitGridView();
  };

  handlePlaceholderSectionTitleOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => this.handleSectionTitleOnClick(event, true);

  handleSectionTitleOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, isPlaceholder?: boolean) => void = (event, isPlaceholder) => {
    const { cardData, isDebugModeEnabled } = this.props;
    const { displayType, isDebugModePlus, maxPageIndex, pageIndex } = this.state;

    event.preventDefault();
    event.stopPropagation();

    if (isDebugModeEnabled && event.ctrlKey) {
      if (event.shiftKey) {
        // Enable debug mode plus for all items in section (live only)
        this.setState({ isDebugModePlus: !isDebugModePlus });
      } else {
        // Show debug info for section
        showDebugInfo(this.props, this.state, this);
      }
      return;
    }

    if (displayType === SectionDisplayType.Grid || cardData || (pageIndex === 0 && maxPageIndex === 0)) {
      return;
    }

    if (!isPlaceholder && displayType !== SectionDisplayType.Grid) {
      this.enterGridView();
    }
  };

  handleNextButtonOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.goToNextPage(getPageStepFromKeyboardModifiers(event));
  };

  handlePrevButtonOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.goToPreviousPage(getPageStepFromKeyboardModifiers(event));
  };

  goToPreviousPage: (pageStep: number) => void = (pageStep) => {
    const { pageIndex } = this.state;

    this.goToPage(Math.max(0, pageIndex - pageStep));
  };

  goToNextPage: (pageStep: number) => void = (pageStep) => {
    const { maxPageIndex, pageIndex } = this.state;

    this.goToPage(Math.min(pageIndex + pageStep, maxPageIndex));
  };

  goToPage: (pageIndex: number, skipIndexSave?: boolean, callback?: BasicFunction) => void = (pageIndex, skipIndexSave, callback) => {
    const {
      localSavePageIndex,
      section: { id },
    } = this.props;
    const { slider, sliderPositions } = this;

    if (!slider || pageIndex < 0 || pageIndex >= sliderPositions.length) {
      if (callback) {
        callback();
      }
      return;
    }

    const posX = sliderPositions[pageIndex];
    slider.style.transform = `translateX(${posX}px) translateZ(0)`;

    this.setState({ pageIndex }, callback);

    if (!skipIndexSave) {
      // Save page index to restore it after player exited
      localSavePageIndex(this.getPageKey(id), pageIndex);
    }

    Messenger.emit(MessengerEvents.SECTION_SLIDING_UPDATE, true);
  };

  checkSavedPageIndex: () => void = () => {
    const {
      section: { id },
      sectionPageIndices,
    } = this.props;
    const { displayType } = this.state;

    const savedPageIndex = sectionPageIndices[this.getPageKey(id)];

    if (displayType === SectionDisplayType.Regular && savedPageIndex) {
      // Restore page index to its value before the player opened
      this.goToPage(savedPageIndex, true);
    }
  };

  getPageKey: (id: string) => string = (id) => {
    const { cardData, hubItem } = this.props;

    if (cardData) {
      return `card_${cardData.item.id}_${id}`;
    }

    if (hubItem) {
      return `hub_${hubItem.id}_${id}`;
    }

    return id;
  };

  handleOnTransitionEnd: () => void = () => {
    Messenger.emit(MessengerEvents.SECTION_SLIDING_UPDATE, false);
  };

  handleGridKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>): void => {
    const { displayType, itemCountPerPage } = this.state;
    const { slider } = this;
    const { key } = event;

    if (!slider) {
      return;
    }

    // Focusable items include both tileContainer (regular items, like movies) and sectionItem (selection items, like channels)
    const focusableSelector = '.sectionItem.selection, .tileContainer:not(.sectionItem.selection *)';
    const focusableItems = Array.from(slider.querySelectorAll<HTMLElement>(focusableSelector)).filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0);

    const totalItems = focusableItems.length;
    if (totalItems === 0) {
      return;
    }

    const activeElement = document.activeElement;
    const currentIndex = focusableItems.findIndex((item) => item === activeElement || item.contains(activeElement));

    if (currentIndex === -1) {
      return;
    }

    // Inter-section navigation (in regular mode)
    if (displayType !== SectionDisplayType.Grid && (key === 'ArrowUp' || key === 'ArrowDown')) {
      const currentSection = slider.closest('.section');
      if (!(currentSection instanceof HTMLElement)) {
        return;
      }

      const allSections = Array.from(document.querySelectorAll<HTMLElement>('.section:not(.placeholder):not(.hidden)'));
      const sectionIndex = allSections.indexOf(currentSection);

      if (sectionIndex === -1) {
        return;
      }

      const targetSection = key === 'ArrowUp' ? allSections[sectionIndex - 1] : allSections[sectionIndex + 1];

      if (targetSection) {
        // Find the active tile (tabIndex="0") in the target section
        const targetTile = targetSection.querySelector('.tileContainer[tabindex="0"], .sectionItem.selection[tabindex="0"]');

        if (targetTile instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();

          targetTile.focus();
          targetTile.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
      return;
    }

    // Intra-section navigation (regular row (left and right) or grid view (all keys))
    let nextIndex = currentIndex;
    const columnCount = displayType === SectionDisplayType.Grid ? itemCountPerPage : totalItems;

    switch (key) {
      case 'ArrowLeft':
        nextIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowRight':
        nextIndex = Math.min(totalItems - 1, currentIndex + 1);
        break;
      case 'ArrowUp':
        if (displayType === SectionDisplayType.Grid) {
          nextIndex = Math.max(0, currentIndex - columnCount);
        }
        break;
      case 'ArrowDown':
        if (displayType === SectionDisplayType.Grid) {
          nextIndex = Math.min(totalItems - 1, currentIndex + columnCount);
        }
        break;
      case 'Home':
        if (displayType === SectionDisplayType.Grid) {
          nextIndex = 0;
        }
        break;
      case 'End':
        if (displayType === SectionDisplayType.Grid) {
          nextIndex = totalItems - 1;
        }
        break;
      default:
        return;
    }

    if (nextIndex !== currentIndex) {
      event.preventDefault();
      event.stopPropagation();

      this.setState({ focusedItemIndex: nextIndex }, () => {
        const targetElement = focusableItems[nextIndex];
        if (targetElement) {
          targetElement.focus();

          // Only scroll into view in grid view, otherwise, let section handle the display of the right page
          if (displayType === SectionDisplayType.Grid) {
            targetElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        }
      });
    }
  };

  handleSectionVisibilityChanged: (isVisible: boolean) => void = (isVisible) => {
    const { updateDimensionsNeeded } = this.state;
    this.isVisible = isVisible;

    if (isVisible && updateDimensionsNeeded) {
      // Became visible
      this.updateDimensions();
    }
  };

  handleOnSwiping: (eventData: SwipeEventData) => void = (eventData) => {
    const { deltaX } = eventData;
    const { displayType, maxPageIndex, pageIndex } = this.state;
    const { slider, sliderPositions } = this;

    if (!slider || maxPageIndex <= 0 || displayType === SectionDisplayType.Grid) {
      return;
    }

    this.setState({ isSwiping: true });

    const posX = sliderPositions[pageIndex];
    const newPosX = posX + deltaX;

    slider.style.transform = `translateX(${newPosX}px) translateZ(0)`;
  };

  handleOnSwiped: (eventData: SwipeEventData) => void = (eventData) => {
    const { dir, velocity } = eventData;
    const { displayType, maxPageIndex } = this.state;

    this.resetSwipeTimeout();
    this.swipeTimeout = setTimeout(() => this.setState({ isSwiping: false }), SWIPE.CooldownTime);

    if (maxPageIndex <= 0 || displayType === SectionDisplayType.Grid) {
      return;
    }

    const pageStep = Math.ceil(velocity / SWIPE.PageVelocityStep);

    if (dir === 'Left') {
      this.goToNextPage(pageStep);
    } else if (dir === 'Right') {
      this.goToPreviousPage(pageStep);
    }
  };

  enterGridView: () => void = () => {
    const {
      localUpdateGridSectionId,
      section: { id },
    } = this.props;

    Messenger.emit(MessengerEvents.MOVE_TO_TOP, true);
    localUpdateGridSectionId(id);

    this.goToPage(0, true, () => {
      this.setState({ displayType: SectionDisplayType.Grid });
    });
  };

  exitGridView: () => void = () => {
    const { localResetGridSectionId } = this.props;
    const { displayType } = this.state;

    if (displayType !== SectionDisplayType.Grid) {
      return;
    }

    localResetGridSectionId();

    this.setState(
      {
        displayType: SectionDisplayType.Regular,
        sortAndFilter: null,
      },
      () => {
        // Will restore vertical scroll position
        Messenger.emit(MessengerEvents.RESTORE_POSITION);

        // Will restore horizontal position of all sections
        this.checkSavedPageIndex();
      },
    );
  };

  onUpdateSortAndFilter: (sortAndFilter: SortAndFilterType) => void = (sortAndFilter) => {
    this.setState(
      produce((draft) => {
        draft.sortAndFilter = sortAndFilter;
      }),
    );
  };

  collapse: () => void = () => {
    this.setState({ displayType: SectionDisplayType.Collapsed });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  hide: () => void = () => {
    this.setState({ displayType: SectionDisplayType.Hidden });
  };

  show: () => void = () => {
    const { displayType, feed } = this.state;

    if ((displayType === SectionDisplayType.Collapsed || displayType === SectionDisplayType.Hidden) && feed && feed.length > 0) {
      this.setState({ displayType: SectionDisplayType.Regular }, this.checkSavedPageIndex);
    }
  };

  renderPlaceholderSection: (sectionTitle: string, tileTypeClass: string) => React.Node = (sectionTitle, tileTypeClass) => {
    const { displayType } = this.state;
    const {
      tileConfig: { type },
    } = this;

    const tileCount = getPlaceholderItemCount(type);
    const placeholderTiles = [];
    for (let i = 0; i < tileCount; ++i) {
      placeholderTiles.push(<PlaceholderItem key={i} />);
    }

    return (
      <div className={clsx('section', 'placeholder', displayType as string, tileTypeClass)}>
        <VisibilityWaypoint onVisibilityChange={this.handleSectionVisibilityChanged} />
        <div className='header'>
          <div className={clsx('sectionTitleContainer', sectionTitle === '' && 'noTitle')} onClick={this.handlePlaceholderSectionTitleOnClick}>
            <div className='sectionTitle'>{sectionTitle}</div>
          </div>
        </div>
        <div>
          <div
            className='sectionSlider'
            ref={(instance: HTMLElement | null) => {
              this.slider = instance;
            }}
          >
            {placeholderTiles}
          </div>
        </div>
      </div>
    );
  };

  customSortFeed: (feed: NETGEM_API_V8_FEED) => NETGEM_API_V8_FEED = (feed) => {
    const { sortAndFilter } = this.state;

    if (sortAndFilter === null) {
      return feed;
    }

    const sortedFeed = [...feed];

    const { direction, kind } = sortAndFilter;

    if (kind === SortKind.Chronological) {
      sortedFeed.sort(compareDate);
    }

    if (direction === SortDirection.Descendant) {
      sortedFeed.reverse();
    }

    return sortedFeed;
  };

  handleItemFocused: (itemIndex: number) => void = (itemIndex) => {
    const { displayType, itemCountPerPage, pageIndex } = this.state;

    this.setState({ focusedItemIndex: itemIndex });

    if (displayType === SectionDisplayType.Grid) {
      return;
    }

    // Check if focused item belongs to a different page than the currently active page
    const itemPage = Math.floor(itemIndex / itemCountPerPage);

    if (itemPage !== pageIndex) {
      this.goToPage(itemPage);
    }
  };

  renderTiles: () => Array<React.Node> = () => {
    const { cardData, isInExploreModal, onItemClick } = this.props;
    const { displayType, feed, focusedItemIndex, isDebugModePlus, isLive, isSwiping, itemCountPerPage, pageIndex, sortAndFilter } = this.state;
    const { tileConfig } = this;

    if (!feed) {
      return [];
    }

    const localFeed = this.customSortFeed(feed);

    const { length: itemCount } = localFeed;
    const indexEnd = displayType === SectionDisplayType.Grid ? itemCount : Math.min((pageIndex + PAGE_DISPLAY_COUNT) * itemCountPerPage, itemCount);
    const tiles: Array<React.Node> = [];

    for (let i = 0; i < indexEnd; ++i) {
      const { [i]: item } = localFeed;

      // Get tile position in section's page
      let pagePosition = PagePosition.Middle;
      if (i % itemCountPerPage === 0) {
        pagePosition = PagePosition.LeftMost;
      } else if ((i + 1) % itemCountPerPage === 0) {
        pagePosition = PagePosition.RightMost;
      }

      tiles.push(
        <Item
          cardData={cardData}
          filter={sortAndFilter?.filter}
          isDebugModePlusForced={Boolean(isLive) && isDebugModePlus}
          isInExploreModal={isInExploreModal}
          isInLiveSection={isLive}
          isSwiping={isSwiping}
          item={item}
          itemIndex={i}
          key={i}
          onItemClick={onItemClick}
          onItemFocused={this.handleItemFocused}
          pagePosition={pagePosition}
          tabIndex={i === focusedItemIndex ? 0 : -1}
          tileConfig={tileConfig}
        />,
      );
    }

    return tiles;
  };

  renderHeader: (title: string, pageIndex: number, maxPageIndex: number) => React.Node = (title, pageIndex, maxPageIndex) => {
    const {
      cardData,
      isDebugModeEnabled,
      section: { id },
    } = this.props;
    const { displayType } = this.state;

    const sortAndFilterElt = isDebugModeEnabled && displayType === SectionDisplayType.Grid ? <SortAndFilter updateSortAndFilter={this.onUpdateSortAndFilter} /> : null;

    const gridViewButton =
      displayType !== SectionDisplayType.Grid && !cardData && maxPageIndex > 0 ? (
        <button className='gridViewButton' onClick={this.handleEnterGridViewButtonOnClick} type='button'>
          <div>{Localizer.localize('common.actions.see_all')}</div>
          <PictoArrowRight aria-hidden forceHoverEffect />
        </button>
      ) : null;

    const paginationElt =
      isDebugModeEnabled && displayType !== SectionDisplayType.Grid && maxPageIndex > 0 ? (
        <div aria-hidden className='sectionPagination'>
          {pageIndex + 1} / {maxPageIndex + 1}
        </div>
      ) : null;

    return (
      <div className='header'>
        <div className={clsx('sectionTitleContainer', gridViewButton !== null && 'hoverable')} onClick={gridViewButton !== null || isDebugModeEnabled ? this.handleSectionTitleOnClick : undefined}>
          <div className='sectionTitle' id={`section_title_${id}`}>
            {title}
          </div>
        </div>
        {gridViewButton}
        {sortAndFilterElt}
        {paginationElt}
      </div>
    );
  };

  render(): React.Node {
    const {
      isGridAvenue,
      section,
      section: { id },
      sectionIndex,
    } = this.props;
    const { displayType, feed, isLoading, maxPageIndex, pageIndex } = this.state;
    const {
      sectionName,
      tileConfig: { imageLayout, type },
    } = this;

    const sectionTitle = getTranslatedText(section.title, Localizer.language);

    if (displayType === SectionDisplayType.Collapsed) {
      // Do not display empty section
      return null;
    }

    // Tile type to CSS class conversion (e.g. 'gemtv.big' -> 'gemtv big')
    const tileTypeClass = getTileTypeClass(type);

    if (isLoading || maxPageIndex === -1 || !feed) {
      // Display placeholder while loading
      return this.renderPlaceholderSection(sectionTitle, tileTypeClass);
    }

    // Display all visible items (+ a little more) or all items in grid view
    const tiles = this.renderTiles();

    const prevButton =
      displayType !== SectionDisplayType.Grid && pageIndex > 0 ? (
        <div aria-hidden className={clsx('navigationButton', 'previous', tileTypeClass)} onClick={this.handlePrevButtonOnClick}>
          <PictoArrowLeft forceHoverEffect />
        </div>
      ) : null;

    const nextButton =
      displayType !== SectionDisplayType.Grid && pageIndex < maxPageIndex ? (
        <div aria-hidden className={clsx('navigationButton', 'next', tileTypeClass)} onClick={this.handleNextButtonOnClick}>
          <PictoArrowRight forceHoverEffect />
        </div>
      ) : null;

    const backButton = !isGridAvenue && displayType === SectionDisplayType.Grid ? <ButtonBack className='backBar' onClick={this.handleExitGridViewButtonOnClick} /> : null;

    return (
      <div
        aria-labelledby={`section_title_${id}`}
        className={clsx('section', displayType as string, tileTypeClass, imageLayout)}
        id={`section_${sectionName}`}
        role='region'
        style={{ zIndex: MAX_Z_INDEX - sectionIndex }}
      >
        <VisibilityWaypoint onVisibilityChange={this.handleSectionVisibilityChanged} />
        {backButton}
        {this.renderHeader(sectionTitle, pageIndex, maxPageIndex)}
        <Swipeable onSwiped={this.handleOnSwiped} onSwiping={this.handleOnSwiping} trackMouse>
          <div
            aria-label={sectionTitle}
            className='sectionSlider'
            onKeyDownCapture={this.handleGridKeyDown}
            onTransitionEnd={this.handleOnTransitionEnd}
            ref={(instance: HTMLElement | null) => {
              this.slider = instance;
            }}
            role='grid'
          >
            {tiles}
          </div>
        </Swipeable>
        {prevButton}
        {nextButton}
      </div>
    );
  }
}

const mapStateToProps: (state: CombinedReducers) => ReduxSectionReducerStateType = (state) => {
  return {
    channels: state.appConfiguration.deviceChannels,
    favoriteList: state.ui.favoriteList || [],
    gridSectionId: state.ui.gridSectionId,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    playingHistory: PlayingHistoryCache.checkGet(state.ui.playingHistory, state.ui.playingHistoryStatus, true),
    playingHistoryStatus: state.ui.playingHistoryStatus,
    recordingsListFeed: state.npvr.npvrRecordingsListFeed,
    sectionPageIndices: state.ui.sectionPageIndices,
    sessionId: state.appConfiguration.sessionId,
    state,
    viewingHistory: ViewingHistoryCache.checkGet(state.ui.viewingHistory, state.ui.viewingHistoryStatus, true),
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
    wishlist: WishlistCache.checkGet(state.ui.wishlist, state.ui.wishlistStatus, true).items,
    wishlistStatus: state.ui.wishlistStatus,
  };
};

const mapDispatchToProps: (dispatch: Dispatch) => ReduxSectionDispatchToPropsType = (dispatch) => {
  return {
    localCleanUpViewingHistory: (viewingHistory: VIEWING_HISTORY_TYPE, feed: NETGEM_API_V8_FEED, locationTypes: Set<NetgemApiV8ItemLocation>): Promise<void> =>
      dispatch(cleanUpViewingHistory(viewingHistory, feed, locationTypes)),

    localResetGridSectionId: (): void => dispatch(resetGridSectionId()),

    localSavePageIndex: (sectionId: string, index: number): void => dispatch(updateSectionPageIndex(sectionId, index)),

    localSendV8LocationCatchupForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationCatchupForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8LocationEpgForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationEpgForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8LocationGameForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationGameForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8LocationVodForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationVodForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, signal?: AbortSignal): Promise<NETGEM_API_V8_FEED> =>
      dispatch(sendV8SectionFeedRequest(section, searchString, signal)),

    localUpdateGridSectionId: (gridSectionId: string | null): void => dispatch(updateGridSectionId(gridSectionId)),
  };
};

const Section: React.ComponentType<SectionPropType> = connect(mapStateToProps, mapDispatchToProps, null)(SectionView);

export { SectionView };
export default Section;
